import { Lock } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Combobox } from '@/components/ui/combobox'

type FieldType = 'text' | 'textarea' | 'number' | 'date' | 'email' | 'phone' | 'select' | 'multi_select' | 'currency' | 'attachment'

type FieldDefinition = {
  id: string
  field_type: FieldType
  label: string
  identifier: string
  required: boolean
  encrypted: boolean
  description?: string | null
  position: number
  options: { id: string; label: string; value: string; position: number }[]
  config: Record<string, unknown>
}

interface FieldInputsProps {
  fields: FieldDefinition[]
  values: Record<string, string | number | string[]>
  onChange: (fieldId: string, value: string | number | string[]) => void
  errors?: Record<string, string>
}

export function FieldInputs({ fields, values, onChange, errors }: FieldInputsProps) {
  const sortedFields = [...fields].sort((a, b) => a.position - b.position)

  return (
    <div className="space-y-6">
      {sortedFields.map(field => (
        <div key={field.id} className="space-y-2">
          <div className="flex items-center gap-2">
            <Label htmlFor={field.id} className="text-sm font-medium">
              {field.label}
            </Label>
            {field.required && <Badge variant="secondary" className="text-xs">Required</Badge>}
            {field.encrypted && <Lock className="h-3 w-3 text-muted-foreground" />}
          </div>

          {field.description && (
            <p className="text-xs text-muted-foreground">{field.description}</p>
          )}

          {renderInput(field, values[field.id], onChange, errors?.[field.id])}

          {errors?.[field.id] && (
            <p className="text-xs text-destructive">{errors[field.id]}</p>
          )}
        </div>
      ))}
    </div>
  )
}

function renderInput(
  field: FieldDefinition,
  value: string | number | string[] | undefined,
  onChange: (fieldId: string, value: string | number | string[]) => void,
  error?: string
) {
  const placeholder = field.config?.placeholder as string | undefined
  const hasError = !!error
  const inputClass = `h-9 ${hasError ? 'border-destructive' : ''}`

  switch (field.field_type) {
    case 'text':
    case 'email':
    case 'phone':
      return (
        <Input
          id={field.id}
          type={field.field_type === 'email' ? 'email' : field.field_type === 'phone' ? 'tel' : 'text'}
          placeholder={placeholder}
          value={(value as string) || ''}
          onChange={e => onChange(field.id, e.target.value)}
          className={inputClass}
        />
      )

    case 'textarea':
      return (
        <Textarea
          id={field.id}
          placeholder={placeholder}
          value={(value as string) || ''}
          onChange={e => onChange(field.id, e.target.value)}
          className={`min-h-20 ${hasError ? 'border-destructive' : ''}`}
        />
      )

    case 'number':
      return (
        <Input
          id={field.id}
          type="number"
          placeholder={placeholder}
          value={(value as number) ?? ''}
          onChange={e => onChange(field.id, e.target.value ? parseFloat(e.target.value) : 0)}
          className={inputClass}
        />
      )

    case 'date':
      return (
        <Input
          id={field.id}
          type="date"
          value={(value as string) || ''}
          onChange={e => onChange(field.id, e.target.value)}
          className={inputClass}
        />
      )

    case 'currency':
      return (
        <div className="flex">
          <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-muted text-sm text-muted-foreground">
            {(field.config?.currency_code as string) || 'USD'}
          </span>
          <Input
            id={field.id}
            type="number"
            step="0.01"
            placeholder="0.00"
            value={(value as number) ?? ''}
            onChange={e => onChange(field.id, e.target.value ? parseFloat(e.target.value) : 0)}
            className={`h-9 flex-1 rounded-l-none ${hasError ? 'border-destructive' : ''}`}
          />
        </div>
      )

    case 'select':
      return (
        <Combobox
          options={field.options.map(opt => ({ value: opt.value, label: opt.label }))}
          value={(value as string) || ''}
          onValueChange={v => onChange(field.id, v)}
          placeholder="Select an option"
          searchPlaceholder="Search options..."
          emptyMessage="No options found."
          className={hasError ? 'border-destructive' : ''}
        />
      )

    case 'multi_select':
      const selectedValues = (value as string[]) || []
      return (
        <div className={`space-y-2 border rounded-md p-3 ${hasError ? 'border-destructive' : ''}`}>
          {field.options.map(opt => (
            <label key={opt.id} className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={selectedValues.includes(opt.value)}
                onCheckedChange={checked => {
                  if (checked) {
                    onChange(field.id, [...selectedValues, opt.value])
                  } else {
                    onChange(field.id, selectedValues.filter(v => v !== opt.value))
                  }
                }}
              />
              <span className="text-sm">{opt.label}</span>
            </label>
          ))}
        </div>
      )

    case 'attachment':
      return (
        <Input
          id={field.id}
          type="file"
          className={inputClass}
          multiple={((field.config?.max_files as number) || 1) > 1}
          onChange={e => {
            const files = Array.from(e.target.files || []).map(f => f.name)
            onChange(field.id, files)
          }}
        />
      )

    default:
      return (
        <Input
          id={field.id}
          value={(value as string) || ''}
          onChange={e => onChange(field.id, e.target.value)}
          className={inputClass}
        />
      )
  }
}

export function validateFields(
  fields: FieldDefinition[],
  values: Record<string, string | number | string[]>
): Record<string, string> {
  const errors: Record<string, string> = {}

  fields.forEach(field => {
    if (field.required) {
      const value = values[field.id]
      const isEmpty = !value || (Array.isArray(value) && value.length === 0) || value === ''
      if (isEmpty) {
        errors[field.id] = `${field.label} is required`
      }
    }
  })

  return errors
}

export type { FieldDefinition, FieldType }
